/**
 * PermissionsStep - Step 5: Configure view-level access control
 *
 * Features:
 * - Permission matrix (rows: groups, columns: view/edit/delete/export, rows own/all)
 * - Add permission button with group selector
 */

import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  IconButton,
  Select,
  Option,
  Checkbox,
  Alert,
  Chip,
  Tooltip,
  Table,
  FormControl,
  FormLabel,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/joy";
import SecurityIcon from "@mui/icons-material/Security";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupIcon from "@mui/icons-material/Group";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useViewBuilderContext } from "../ViewBuilderContext";
import { useGroups } from "../../../hooks/useGroups";
import type { RowScope, ViewBuilderPermission } from "../../../types";

interface PermissionsStepProps {
  workspaceId: string;
}

export default function PermissionsStep({ workspaceId }: PermissionsStepProps) {
  const { state, addPermission, updatePermission, removePermission } =
    useViewBuilderContext();

  // Fetch groups from API
  const {
    groups,
    loading: groupsLoading,
    error: groupsError,
  } = useGroups(workspaceId);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  // Get groups that don't already have permissions
  const availableGroups = groups.filter(
    (g) => !state.permissions.find((p) => p.groupId === g.id),
  );

  const handleAddPermission = () => {
    if (!selectedGroupId) return;

    const group = groups.find((g) => g.id === selectedGroupId);
    if (!group) return;

    addPermission(selectedGroupId, group.display_name);
    setSelectedGroupId(null);
    setIsAddModalOpen(false);
  };

  const handlePermissionChange = (
    permissionId: string,
    field: keyof ViewBuilderPermission,
    value: boolean,
  ) => {
    // If disabling canView, also disable edit/delete/export (cascading)
    if (field === "canView" && !value) {
      updatePermission(permissionId, {
        canView: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
      });
    } else {
      updatePermission(permissionId, { [field]: value });
    }
  };

  return (
    <Stack spacing={3}>
      {/* Info Card */}
      <Card variant="soft" color="primary">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center">
            <SecurityIcon />
            <Box>
              <Typography level="title-sm">Access Permissions</Typography>
              <Typography level="body-xs">
                Control who can access this view and what actions they can
                perform. Permissions are optional - leave empty for default
                access rules.
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Permissions Table */}
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Typography level="title-lg">Permissions</Typography>
            <Chip size="sm" variant="soft">
              {state.permissions.length}
            </Chip>
            <Box sx={{ flex: 1 }} />
            <Button
              size="sm"
              variant="soft"
              startDecorator={
                groupsLoading ? <CircularProgress size="sm" /> : <AddIcon />
              }
              onClick={() => setIsAddModalOpen(true)}
              disabled={groupsLoading || availableGroups.length === 0}
            >
              Add Permission
            </Button>
          </Stack>

          {state.permissions.length === 0 ? (
            <Box
              sx={{
                py: 4,
                textAlign: "center",
                color: "text.tertiary",
              }}
            >
              <SecurityIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
              <Typography level="body-sm">
                No permissions configured. Default workspace access rules will
                apply.
              </Typography>
              <Typography level="body-xs" sx={{ mt: 1 }}>
                Click "Add Permission" to customize access for specific groups.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table
                sx={{
                  "& th": { fontWeight: "lg", bgcolor: "neutral.50" },
                  "& td, & th": { textAlign: "center" },
                  "& td:first-of-type, & th:first-of-type": {
                    textAlign: "left",
                  },
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: "25%" }}>Group</th>
                    <th style={{ width: "12%" }}>
                      <Tooltip title="Can view records">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          justifyContent="center"
                        >
                          <VisibilityIcon fontSize="small" />
                          <Typography level="body-xs">View</Typography>
                        </Stack>
                      </Tooltip>
                    </th>
                    <th style={{ width: "12%" }}>
                      <Tooltip title="Can edit records">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          justifyContent="center"
                        >
                          <EditIcon fontSize="small" />
                          <Typography level="body-xs">Edit</Typography>
                        </Stack>
                      </Tooltip>
                    </th>
                    <th style={{ width: "12%" }}>
                      <Tooltip title="Can delete records">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          justifyContent="center"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                          <Typography level="body-xs">Delete</Typography>
                        </Stack>
                      </Tooltip>
                    </th>
                    <th style={{ width: "12%" }}>
                      <Tooltip title="Can export records">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          justifyContent="center"
                        >
                          <FileDownloadIcon fontSize="small" />
                          <Typography level="body-xs">Export</Typography>
                        </Stack>
                      </Tooltip>
                    </th>
                    <th style={{ width: "12%" }}>
                      <Tooltip title="On a personal view (one with a user-identifier field): each member sees only their own rows, or every row">
                        <Typography level="body-xs">Rows</Typography>
                      </Tooltip>
                    </th>
                    <th style={{ width: "7%" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {state.permissions.map((permission) => (
                    <tr key={permission.id}>
                      <td>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <GroupIcon
                            fontSize="small"
                            sx={{ color: "neutral.500" }}
                          />
                          <Typography level="body-sm" fontWeight="md">
                            {permission.groupName ||
                              groups.find((g) => g.id === permission.groupId)
                                ?.display_name ||
                              permission.groupId}
                          </Typography>
                        </Stack>
                      </td>
                      <td>
                        <Checkbox
                          size="sm"
                          checked={permission.canView}
                          onChange={(e) =>
                            handlePermissionChange(
                              permission.id,
                              "canView",
                              e.target.checked,
                            )
                          }
                        />
                      </td>
                      <td>
                        <Checkbox
                          size="sm"
                          checked={permission.canEdit}
                          onChange={(e) =>
                            handlePermissionChange(
                              permission.id,
                              "canEdit",
                              e.target.checked,
                            )
                          }
                          disabled={!permission.canView}
                        />
                      </td>
                      <td>
                        <Checkbox
                          size="sm"
                          checked={permission.canDelete}
                          onChange={(e) =>
                            handlePermissionChange(
                              permission.id,
                              "canDelete",
                              e.target.checked,
                            )
                          }
                          disabled={!permission.canView}
                        />
                      </td>
                      <td>
                        <Checkbox
                          size="sm"
                          checked={permission.canExport}
                          onChange={(e) =>
                            handlePermissionChange(
                              permission.id,
                              "canExport",
                              e.target.checked,
                            )
                          }
                          disabled={!permission.canView}
                        />
                      </td>
                      <td>
                        <Select
                          size="sm"
                          value={permission.rowScope}
                          disabled={!permission.canView}
                          slotProps={{
                            button: { "aria-label": "Rows this group sees" },
                          }}
                          onChange={(_, value) =>
                            value &&
                            updatePermission(permission.id, {
                              rowScope: value as RowScope,
                            })
                          }
                        >
                          <Option value="own">Own</Option>
                          <Option value="all">All</Option>
                        </Select>
                      </td>
                      <td>
                        <Tooltip title="Remove permission">
                          <IconButton
                            size="sm"
                            variant="soft"
                            color="danger"
                            onClick={() => removePermission(permission.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add Permission Modal */}
      <Modal open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <DialogTitle>Add Permission</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography level="body-sm">
                Select a group to grant permissions for this view.
              </Typography>

              {groupsError && (
                <Alert color="danger" size="sm">
                  Failed to load groups: {groupsError}
                </Alert>
              )}

              <FormControl>
                <FormLabel>Group</FormLabel>
                <Select
                  value={selectedGroupId}
                  onChange={(_, v) => setSelectedGroupId(v)}
                  placeholder={
                    groupsLoading ? "Loading groups..." : "Select a group..."
                  }
                  disabled={groupsLoading}
                >
                  {availableGroups.map((group) => (
                    <Option key={group.id} value={group.id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <GroupIcon fontSize="small" />
                        <span>{group.display_name}</span>
                      </Stack>
                    </Option>
                  ))}
                </Select>
              </FormControl>

              {!groupsLoading && groups.length === 0 && !groupsError && (
                <Alert color="neutral" size="sm">
                  No groups have been created in this workspace yet.
                </Alert>
              )}

              {!groupsLoading &&
                groups.length > 0 &&
                availableGroups.length === 0 && (
                  <Alert color="neutral" size="sm">
                    All available groups already have permissions configured.
                  </Alert>
                )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setIsAddModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleAddPermission}
              disabled={!selectedGroupId}
            >
              Add Permission
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Stack>
  );
}
